import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mixpanel } from '../../utils/mixpanelIntegration';
import UserService from '../../services/api/UserService';
import '../../sass/layout/flipbook/flipbook.css';

const FlipBook = ({ pdfUrl, width = '100%', height = '1200px', className = '' }) => {
  const { t } = useTranslation();
  const flipBookRef = useRef(null);
  const pdfFlipInstance = useRef(null);
  const [flipbookKey] = useState(Date.now());


  useEffect(() => {
    // Load PDF-Flip scripts dynamically
    // Load PDF-Flip scripts dynamically
    const loadPDFFlip = async () => {
      try {
        // Check if jQuery is already loaded
        if (!window.jQuery) {
          const jqueryScript = document.createElement('script');
          jqueryScript.src = '/PDF-Flip/pflip/js/libs/jquery.min.js';
          jqueryScript.async = false;
          document.head.appendChild(jqueryScript);

          await new Promise((resolve, reject) => {
            jqueryScript.onload = resolve;
            jqueryScript.onerror = reject;
          });
        }

        // Load PDF-Flip main script
        if (!window.pdfflip) {
          const pdfFlipScript = document.createElement('script');
          pdfFlipScript.src = '/PDF-Flip/pflip/js/pdfflip.js';
          pdfFlipScript.async = false;
          document.head.appendChild(pdfFlipScript);

          await new Promise((resolve, reject) => {
            pdfFlipScript.onload = () => {
              // Simple delay to ensure script is fully processed
              setTimeout(resolve, 200);
            };
            pdfFlipScript.onerror = reject;
          });
        }

        // Load CSS
        if (!document.querySelector('link[href="/PDF-Flip/pflip/css/pdfflip.css"]')) {
          const cssLink = document.createElement('link');
          cssLink.rel = 'stylesheet';
          cssLink.href = '/PDF-Flip/pflip/css/pdfflip.css';
          document.head.appendChild(cssLink);
        }

        // Initialize PDF-Flip with a small delay to ensure DOM is ready
        setTimeout(() => {
          initializePDFFlip();
        }, 300);
      } catch (error) {
        console.error('Error loading PDF-Flip:', error);
      }
    };

    const initializePDFFlip = () => {
      if (flipBookRef.current && window.pdfflip && window.jQuery) {
        // Clear any existing instance
        if (pdfFlipInstance.current) {
          try {
            pdfFlipInstance.current.dispose();
          } catch (e) {
            console.warn('Error disposing previous PDF-Flip instance:', e);
          }
        }

        // Clear the DOM element content
        const $ = window.jQuery;
        $(flipBookRef.current).empty();

        // Set PDF source from prop (already includes token when needed)
        const finalPdfUrl = pdfUrl || '/PDF-Flip/PDF.pdf';
        console.log('finalPdfUrl', finalPdfUrl)
        console.log('Initializing PDF-Flip')

        flipBookRef.current.setAttribute('source', finalPdfUrl);

        // Configure options
        const options = {
          openPage: 1,
          height: height,
          enableSound: true,
          downloadEnable: true,
          direction: window.pdfflip?.DIRECTION?.LTR || 1,
          autoPlay: true,
          autoPlayStart: false,
          autoPlayDuration: 9000,
          autoEnableOutline: false,
          autoEnableThumbnail: false,
          text: {
            toggleSound: t('flipbook.toggleSound', 'Sound'),
            toggleThumbnails: t('flipbook.toggleThumbnails', 'Thumbnails'),
            toggleOutline: t('flipbook.toggleOutline', 'Contents'),
            previousPage: t('flipbook.previousPage', 'Previous Page'),
            nextPage: t('flipbook.nextPage', 'Next Page'),
            toggleFullscreen: t('flipbook.toggleFullscreen', 'Fullscreen'),
            zoomIn: t('flipbook.zoomIn', 'Zoom In'),
            zoomOut: t('flipbook.zoomOut', 'Zoom Out'),
            downloadPDFFile: t('flipbook.downloadPDFFile', 'Download PDF'),
            gotoFirstPage: t('flipbook.gotoFirstPage', 'First Page'),
            gotoLastPage: t('flipbook.gotoLastPage', 'Last Page'),
            play: t('flipbook.play', 'AutoPlay On'),
            pause: t('flipbook.pause', 'AutoPlay Off'),
            share: t('flipbook.share', 'Share')
          },
          hard: 'none',
          overwritePDFOutline: true,
          duration: 1000,
          pageMode: window.pdfflip?.PAGE_MODE?.DOUBLE || 2,
          singlePageMode: window.pdfflip?.SINGLE_PAGE_MODE?.ZOOM || 2,
          transparent: false,
          scrollWheel: true,
          zoomRatio: 1.5,
          maxTextureSize: 1600,
          backgroundImage: '/PDF-Flip/pflip/background.jpg',
          backgroundColor: '#fff',
          controlsPosition: window.pdfflip?.CONTROLSPOSITION?.BOTTOM || 1,
          // allControls: 'thumbnail,play,startPage,altPrev,pageNumber,altNext,endPage,zoomIn,zoomOut,fullScreen,download,sound,share',
          allControls: 'thumbnail,play,startPage,altPrev,pageNumber,altNext,zoomIn,zoomOut,fullScreen',
          hideControls: 'outline'
        };

        // Set global options
        window.option_PDFF = options;
        window.pdfflipLocation = '/PDF-Flip/pflip/';

        // Initialize PDF-Flip
        try {
          const $ = window.jQuery;

          // Try different initialization approaches
          if ($.fn.flipBook) {
            // Method 1: Standard jQuery plugin approach (correct plugin name is flipBook)
            pdfFlipInstance.current = $(flipBookRef.current).flipBook(finalPdfUrl, options);
          } else if ($.fn.pdfflip) {
            // Method 2: Alternative plugin name
            $(flipBookRef.current).pdfflip(options);
            pdfFlipInstance.current = $(flipBookRef.current).data('pdfflip');
          } else if (window.pdfflip && typeof window.pdfflip === 'function') {
            // Method 3: Direct pdfflip function call
            pdfFlipInstance.current = window.pdfflip(flipBookRef.current, options);
          } else if (window.pdfflip && window.pdfflip.init) {
            // Method 4: Using init method
            pdfFlipInstance.current = window.pdfflip.init(flipBookRef.current, options);
          } else {
            throw new Error('No valid PDF-Flip initialization method found');
          }

          console.log('PDF-Flip initialized successfully');

          // Try to bind to PDF-Flip events if available
          try {
            const $ = window.jQuery;
            const flipElement = $(flipBookRef.current);

            // PDF-Flip doesn't emit jQuery events, so we'll use polling to detect changes
            // and hook into the PDF-Flip instance methods if possible

            if (pdfFlipInstance.current) {
              console.log('pdfFlipInstance.current', pdfFlipInstance.current)
              // Store reference to original methods to track usage
              const originalNext = pdfFlipInstance.current.next;
              const originalPrev = pdfFlipInstance.current.prev;
              const originalGotoPage = pdfFlipInstance.current.gotoPage;

              // Override methods to track events
              if (originalNext) {
                pdfFlipInstance.current.next = function(...args) {
                  const result = originalNext.apply(this, args);
                  Mixpanel.track("FlipBook Next Page Event", {
                    pdfUrl: finalPdfUrl,
                    isLogged: UserService.isLoggedIn(),
                    interactionType: 'next_page_event'
                  });
                  return result;
                };
              }

              if (originalPrev) {
                pdfFlipInstance.current.prev = function(...args) {
                  const result = originalPrev.apply(this, args);
                  Mixpanel.track("FlipBook Previous Page Event", {
                    pdfUrl: finalPdfUrl,
                    isLogged: UserService.isLoggedIn(),
                    interactionType: 'prev_page_event'
                  });
                  return result;
                };
              }

              if (originalGotoPage) {
                pdfFlipInstance.current.gotoPage = function(...args) {
                  const result = originalGotoPage.apply(this, args);
                  Mixpanel.track("FlipBook Page Changed", {
                    targetPage: args[0],
                    pdfUrl: finalPdfUrl,
                    isLogged: UserService.isLoggedIn(),
                    interactionType: 'page_change_event'
                  });
                  return result;
                };
              }

            }

          } catch (eventError) {
            console.log('PDF-Flip events not available or failed to bind:', eventError);
          }
        } catch (error) {
          console.error('Error initializing PDF-Flip:', error);
          console.log('Available window.pdfflip:', window.pdfflip);
          console.log('jQuery.fn.pdfflip available:', !!(window.jQuery && window.jQuery.fn.pdfflip));
        }
      } else {
        console.warn('PDF-Flip initialization skipped - missing dependencies:', {
          flipBookRef: !!flipBookRef.current,
          pdfflip: !!window.pdfflip,
          jQuery: !!window.jQuery,
          pdfflipType: typeof window.pdfflip,
          pdfflipProps: window.pdfflip ? Object.keys(window.pdfflip).slice(0, 10) : []
        });
      }
    };

    // Only initialize when we have a PDF URL
    if (pdfUrl) {
      loadPDFFlip();
    }

    // Cleanup on unmount
    return () => {
      if (pdfFlipInstance.current) {
        try {
          pdfFlipInstance.current.dispose();
        } catch (e) {
          console.warn('Error disposing PDF-Flip instance on cleanup:', e);
        }
      }
    };
  }, [pdfUrl, height, t, flipbookKey]);


  // Renders nothing until a valid pdfUrl is provided
  if (!pdfUrl) return null;

  return (
    <div className={`flipbook-wrapper ${className}`} style={{ width }}>

      {/* FlipBook Container */}
      <div className={`flipbook-container`} style={{ width: '100%', height }}>
        <div
          ref={flipBookRef}
          className="PDFFlip"
          id={`flipbook-${flipbookKey}`}
          style={{ width: '100%', height: '100%' }}
        />
      </div>

    </div>
  );
};

export default FlipBook;
